import React from 'react';
import {
  Search, Activity, Eye, XCircle, RefreshCw, Download,
  Shield, UserPlus, Building2, Key
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const getActionCategory = (action) => {
  if (action.startsWith('user.login') || action.startsWith('user.logout')) {
    return { bg: 'bg-sky-50', icon: <Key size={14} className="text-sky-600" />, badgeBg: 'bg-sky-50', badgeText: 'text-sky-700' };
  }
  if (action.startsWith('user.')) {
    return { bg: 'bg-blue-50', icon: <UserPlus size={14} className="text-blue-600" />, badgeBg: 'bg-blue-50', badgeText: 'text-blue-700' };
  }
  if (action.startsWith('role.') || action.startsWith('permission.')) {
    return { bg: 'bg-purple-50', icon: <Shield size={14} className="text-purple-600" />, badgeBg: 'bg-purple-50', badgeText: 'text-purple-700' };
  }
  if (action.startsWith('org.') || action.startsWith('department.')) {
    return { bg: 'bg-emerald-50', icon: <Building2 size={14} className="text-emerald-600" />, badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700' };
  }
  if (action.startsWith('lei.') || action.startsWith('kpi.') || action.startsWith('cohort.') || action.startsWith('data.')) {
    return { bg: 'bg-amber-50', icon: <Download size={14} className="text-amber-600" />, badgeBg: 'bg-amber-50', badgeText: 'text-amber-700' };
  }
  if (action.startsWith('dashboard.') || action.startsWith('health_dashboard.') || action.startsWith('analytics.') || action.startsWith('scenario.')) {
    return { bg: 'bg-teal-50', icon: <Eye size={14} className="text-teal-600" />, badgeBg: 'bg-teal-50', badgeText: 'text-teal-700' };
  }
  if (action.startsWith('report.')) {
    return { bg: 'bg-rose-50', icon: <Download size={14} className="text-rose-600" />, badgeBg: 'bg-rose-50', badgeText: 'text-rose-700' };
  }
  return { bg: 'bg-gray-50', icon: <Activity size={14} className="text-gray-500" />, badgeBg: 'bg-gray-100', badgeText: 'text-gray-600' };
};

const formatAuditDescription = (log) => {
  const parts = [];
  if (log.target_name) parts.push(log.target_name);
  if (log.changes && Object.keys(log.changes).length > 0) {
    const fields = Object.keys(log.changes).slice(0, 2).join(', ');
    parts.push(`changed: ${fields}`);
  }
  if (log.metadata) {
    if (log.metadata.role) parts.push(`role: ${log.metadata.role}`);
    if (log.metadata.time_range) parts.push(`range: ${log.metadata.time_range}`);
    if (log.metadata.sheets) parts.push(`${log.metadata.sheets} sheets`);
  }
  return parts.length > 0 ? parts.join(' \u00B7 ') : log.action.replace(/\./g, ' ');
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

export const AuditLogTab = ({
  auditSearch, setAuditSearch,
  auditCategory, setAuditCategory,
  auditDateRange, setAuditDateRange,
  auditPage, setAuditPage,
  auditLogs, auditTotal, auditStats, auditCategories,
  fetchAuditLogs,
}) => {
  return (
    <>
      {/* Audit Filters */}
      <Card className="mb-4">
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by user, action, or target..."
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setAuditPage(0); fetchAuditLogs({ page: 0 }); } }}
                  className="pl-8 h-9"
                  data-testid="audit-search-input"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Category</label>
              <Select value={auditCategory} onValueChange={(v) => { setAuditCategory(v); setAuditPage(0); fetchAuditLogs({ category: v, page: 0 }); }}>
                <SelectTrigger className="h-9" data-testid="audit-category-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(auditCategories).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[140px]">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Time Range</label>
              <Select value={auditDateRange} onValueChange={(v) => { setAuditDateRange(v); setAuditPage(0); fetchAuditLogs({ dateRange: v, page: 0 }); }}>
                <SelectTrigger className="h-9" data-testid="audit-date-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="h-9" onClick={() => fetchAuditLogs()} data-testid="audit-refresh-btn">
              <RefreshCw size={14} className="mr-1.5" />
              Refresh
            </Button>
            <Button variant="outline" className="h-9" onClick={() => {
              setAuditSearch(''); setAuditCategory('all'); setAuditDateRange('7d'); setAuditPage(0);
              fetchAuditLogs({ search: '', category: 'all', dateRange: '7d', page: 0 });
            }} data-testid="audit-clear-filters-btn">
              <XCircle size={14} className="mr-1.5" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {auditStats.top_actions && auditStats.top_actions.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-gray-500 mb-0.5">Total Events</p>
              <p className="text-xl font-bold" data-testid="audit-total-count">{auditTotal}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-gray-500 mb-0.5">Unique Users</p>
              <p className="text-xl font-bold" data-testid="audit-unique-users">{auditStats.unique_actors || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-gray-500 mb-0.5">Top Action</p>
              <p className="text-sm font-semibold truncate" data-testid="audit-top-action">
                {auditStats.top_actions?.[0]?.action?.replace('.', ' ') || '-'}
              </p>
              <p className="text-xs text-gray-400">{auditStats.top_actions?.[0]?.count || 0} events</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit Log Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Audit Trail
              <span className="text-sm font-normal text-gray-400 ml-2">
                {auditTotal} event{auditTotal !== 1 ? 's' : ''} found
              </span>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5 max-h-[520px] overflow-y-auto" data-testid="audit-log-list">
            {auditLogs.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Activity size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No audit events found</p>
                <p className="text-sm mt-1">Try adjusting your filters or time range</p>
              </div>
            ) : (
              auditLogs.map(log => {
                const actionCat = getActionCategory(log.action);
                return (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50/50 transition-colors"
                    data-testid={`audit-event-${log.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${actionCat.bg}`}>
                      {actionCat.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{log.actor_email || log.actor_id}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${actionCat.badgeBg} ${actionCat.badgeText} border-0`}>
                          {log.action.replace(/\./g, ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {formatAuditDescription(log)}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span>{formatTimeAgo(log.created_at)}</span>
                        {log.ip_address && log.ip_address !== 'None' && (
                          <span className="flex items-center gap-1">
                            <Eye size={10} />
                            {log.ip_address}
                          </span>
                        )}
                        {log.organization_id && (
                          <span className="truncate max-w-[120px]">{log.organization_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {auditTotal > 50 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t" data-testid="audit-pagination">
              <p className="text-sm text-gray-500">
                Showing {auditPage * 50 + 1}-{Math.min((auditPage + 1) * 50, auditTotal)} of {auditTotal}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditPage === 0}
                  onClick={() => { setAuditPage(p => p - 1); fetchAuditLogs({ page: auditPage - 1 }); }}
                  data-testid="audit-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(auditPage + 1) * 50 >= auditTotal}
                  onClick={() => { setAuditPage(p => p + 1); fetchAuditLogs({ page: auditPage + 1 }); }}
                  data-testid="audit-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
